import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CONSENT_MIN_AGE,
  CONSENT_PURPOSES,
  RECORDABLE_CONSENT_METHODS,
  type ConsentInput,
  type ConsentPurpose,
  type ConsentState,
  type HouseConsents,
  type HouseDetail,
} from '@mahallu/shared';
import { ApiError, loadErrorKey, privacy, useHouseConsents } from '../api';
import { CheckGroup, FormFooter, FormSection, Modal, SelectField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const STATE_TONE = { GIVEN: 'ok', WITHDRAWN: 'bad', NONE: 'neutral' } as const;
type Method = ConsentInput['method'];

function ConsentForm({ house, consents, state, onClose }: { house: HouseDetail; consents: HouseConsents; state: ConsentState; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const thisYear = new Date().getFullYear();
  const adults = house.members.filter((m) => m.status === 'ALIVE' && (m.birthYear === null || thisYear - m.birthYear >= CONSENT_MIN_AGE));
  const head = adults.find((m) => m.relation === 'HEAD');
  // Offer only what would change: not-yet-given purposes to give, given ones to withdraw.
  const current = (p: ConsentPurpose) => consents.purposes.find((x) => x.purpose === p)!;
  const open = CONSENT_PURPOSES.filter((p) => (state === 'GIVEN' ? current(p).state !== 'GIVEN' || current(p).noticeVersion !== consents.currentNotice : current(p).state === 'GIVEN'));
  const [purposes, setPurposes] = useState<ConsentPurpose[]>(state === 'GIVEN' && open.includes('REGISTER') ? ['REGISTER'] : []);
  const [by, setBy] = useState(head?.id ?? '');
  const [method, setMethod] = useState<Method | ''>('');
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(false);
  const save = useMutation({
    mutationFn: () => privacy.recordConsent(house.id, { purposes, state, method: method as Method, givenByMemberId: by, note: note.trim() || null }),
    onSuccess: async (updated) => {
      qc.setQueryData(['houses', 'consents', house.id], updated);
      // The house's consent badge and the register's consent tile may change.
      await qc.invalidateQueries({ queryKey: ['houses'] });
      onClose();
    },
  });
  return (
    <Modal
      title={state === 'GIVEN' ? t('consent.give') : t('consent.withdraw')}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={state === 'GIVEN' ? t('consent.give') : t('consent.withdraw')} error={missing ? t('consent.pickPurpose') : save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          setMissing(!purposes.length);
          if (purposes.length) save.mutate();
        }}
      >
        {state === 'GIVEN' ? <p className="callout small">{t('consent.giveHint', { n: consents.currentNotice ?? 0 })}</p> : null}
        <FormSection title={t('consent.section')}>
          <CheckGroup<ConsentPurpose>
            label={t('consent.purposes')}
            value={purposes}
            onChange={setPurposes}
            required
            full
            options={CONSENT_PURPOSES.map((p) => ({ value: p, label: t(`purpose.${p}`), disabled: !open.includes(p) }))}
          />
          <SelectField
            label={t('consent.person')}
            value={by}
            onChange={setBy}
            required
            empty={t('form.choose')}
            hint={t('consent.personHint')}
            options={adults.map((m) => ({ value: m.id, label: `${m.nameEn} · ${t(`rel.${m.relation}`)}` }))}
          />
          <SelectField<Method> label={t('consent.method')} value={method} onChange={setMethod} required empty={t('form.choose')} options={RECORDABLE_CONSENT_METHODS.map((m) => ({ value: m, label: t(`cmethod.${m}`) }))} />
          <TextField label={t('consent.note')} value={note} onChange={setNote} maxLength={500} full />
        </FormSection>
      </form>
    </Modal>
  );
}

/** House drawer › Consent: what this house agreed to, against which notice, and the full history. */
export function ConsentTab({ house, canEdit }: { house: HouseDetail; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const consents = useHouseConsents(house.id);
  const [form, setForm] = useState<ConsentState | null>(null);
  const c = consents.data;
  if (consents.isError) return <p className="note">{t(loadErrorKey(consents.error))}</p>;
  if (!c) return <p className="note">{t('drawer.loading')}</p>;
  const anyGiven = c.purposes.some((p) => p.state === 'GIVEN');
  return (
    <>
      {c.currentNotice === null ? <p className="callout small">{t('consent.noNotice')}</p> : null}
      {canEdit ? (
        <div className="actions">
          <button type="button" className="btn sm primary" disabled={c.currentNotice === null} onClick={() => setForm('GIVEN')}>
            <Icon name="check" />
            {t('consent.give')}
          </button>
          <button type="button" className="btn sm" disabled={!anyGiven} onClick={() => setForm('WITHDRAWN')}>
            {t('consent.withdraw')}
          </button>
        </div>
      ) : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('consent.purpose')}</th>
                <th>{t('consent.state')}</th>
                <th>{t('consent.person')}</th>
                <th>{t('consent.notice')}</th>
                <th>{t('consent.when')}</th>
              </tr>
            </thead>
            <tbody>
              {c.purposes.map((p) => (
                <tr key={p.purpose}>
                  <td>{t(`purpose.${p.purpose}`)}</td>
                  <td>
                    <Badge tone={STATE_TONE[p.state]}>{t(`consent.${p.state}`)}</Badge>
                    {p.state === 'GIVEN' && c.currentNotice !== null && p.noticeVersion !== c.currentNotice ? (
                      <div className="note">{t('consent.olderNotice')}</div>
                    ) : null}
                  </td>
                  <td>{p.givenBy ?? (p.method ? t(`cmethod.${p.method}`) : '—')}</td>
                  <td className="num">{p.noticeVersion ? t('consent.noticeV', { n: p.noticeVersion }) : '—'}</td>
                  <td className="num">{p.recordedAt ? dateTime(p.recordedAt, i18n.language) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {c.history.length ? (
        <>
          <h4 className="sub-h">{t('consent.history')}</h4>
          <ul className="history">
            {c.history.map((e) => (
              <li key={e.id}>
                <span className="num">{dateTime(e.recordedAt, i18n.language)}</span> · {t(`purpose.${e.purpose}`)} · <b>{t(`consent.${e.state}`)}</b> · {t(`cmethod.${e.method}`)}
                {e.givenBy ? ` · ${e.givenBy}` : ''}
                {e.noticeVersion ? ` · ${t('consent.noticeV', { n: e.noticeVersion })}` : ''}
                {e.note ? <div className="note">{e.note}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {form ? <ConsentForm house={house} consents={c} state={form} onClose={() => setForm(null)} /> : null}
    </>
  );
}
